import {
  subDays,
  subMonths,
  subYears,
  startOfDay,
  endOfDay,
  format,
  isBefore,
  isEqual,
} from "date-fns";
import { dateFnsLocale, isEnglish, t } from "i18n";

const PRESET_IDS = ["7d", "14d", "30d", "90d", "6m", "1y"];

/**
 * Returns the translated label for a preset period ID (e.g. "Last 7 days")
 * @param {string} periodId
 * @returns {string}
 */
export const getPresetLabel = (periodId) =>
  t(`navigation.periodPicker.presets.${periodId}`);

/**
 * Whether the given period ID is one of the preset periods
 * @param {string} periodId
 * @returns {boolean}
 */
export const isPresetPeriod = (periodId) => PRESET_IDS.includes(periodId);

/**
 * Returns an array of preset period definitions
 */
export const getPresetPeriods = () =>
  PRESET_IDS.map((id) => ({ id, label: getPresetLabel(id) }));

/**
 * Calculates start and end dates for a given period ID
 * @param {string} periodId - The period identifier (e.g., "7d", "30d", "6m")
 * @returns {{ startDate: Date, endDate: Date }}
 */
export const calculatePeriodDates = (periodId) => {
  const end = endOfDay(new Date());
  let start;

  switch (periodId) {
    case "7d":
      start = subDays(end, 7);
      break;
    case "14d":
      start = subDays(end, 14);
      break;
    case "30d":
      start = subDays(end, 30);
      break;
    case "90d":
      start = subDays(end, 90);
      break;
    case "6m":
      start = subMonths(end, 6);
      break;
    case "1y":
      start = subYears(end, 1);
      break;
    default:
      start = subDays(end, 7);
  }

  return { startDate: startOfDay(start), endDate: end };
};

/**
 * Formats a date for display (e.g., "Jan 15, 2026")
 * @param {Date} date
 * @returns {string}
 */
export const formatDateDisplay = (date) => {
  return format(date, isEnglish() ? "MMM d, yyyy" : "PP", {
    locale: dateFnsLocale(),
  });
};

/**
 * Formats a date for short display (e.g., "Jan 15")
 * @param {Date} date
 * @returns {string}
 */
export const formatDateShort = (date) => {
  return format(date, isEnglish() ? "MMM d" : "d MMM", {
    locale: dateFnsLocale(),
  });
};

/**
 * Formats a date as a YYYY-MM-DD API query param using the LOCAL calendar day.
 * Using date-fns `format` (local) instead of `toISOString()` (UTC) avoids an
 * off-by-one where a date picked near midnight west of UTC serializes to the
 * previous/next day.
 * @param {Date} date
 * @returns {string}
 */
export const formatDateParam = (date) => {
  return format(date, "yyyy-MM-dd");
};

/**
 * Validates that a date range is valid (start <= end and end is not in the future)
 * @param {Date} start
 * @param {Date} end
 * @returns {boolean}
 */
export const isValidDateRange = (start, end) => {
  if (!start || !end) return false;
  const today = endOfDay(new Date());
  return (
    (isBefore(start, end) || isEqual(start, end)) &&
    (isBefore(end, today) || isEqual(end, today))
  );
};

/**
 * Gets the default period object (Last 30 days)
 * @returns {{ periodId: string, label: string, startDate: Date, endDate: Date }}
 */
export const getDefaultPeriod = () => {
  const dates = calculatePeriodDates("30d");
  return {
    periodId: "30d",
    label: getPresetLabel("30d"),
    ...dates,
  };
};
